/**
 * FinancialLayout - Layout específico para o módulo Financeiro
 * 
 * Baseado no CrmLayout, com sidebar específica do Financeiro.
 * Usa FinancialSidebar para navegação lateral.
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do layout financeiro
 */

import { ReactNode, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
// Navegação 2.0 — rail de categorias + painel de submenus (substitui sidebars por módulo)
import { AppShellSidebar } from '@/components/navigation/AppShellSidebar';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { useAssertNotSharedRoute } from '@/hooks/useAssertNotSharedRoute';
import { ThemeToggle } from '@/components/ui/theme-toggle';

import { TooltipProvider } from '@/components/ui/tooltip';
import { UserProfileDialog } from '@/components/tenant/UserProfileDialog';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import { UserMenu } from '@/components/tenant/UserMenu';

import { NotificationBell } from '@/components/tenant/notifications/NotificationBell';
import { AppLauncherButton } from '@/components/shortcuts/AppLauncherButton';
import { QuickCreateButton } from '@/components/quick-create/QuickCreateButton';
import { HeaderOverflowMenu } from '@/components/tenant/HeaderOverflowMenu';
// Bloco central compartilhado do header (pesquisa global + botão "+")
import { HeaderSearchSlot } from '@/components/layouts/HeaderSearchSlot';

import { PaymentGuard } from '@/components/guards/PaymentGuard';

interface FinancialLayoutProps {
  children: ReactNode;
}

/**
 * Obtém a role principal do usuário no tenant atual
 */
const getUserRole = (roles: { role: string; tenant_id: string | null }[], tenantId?: string): 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user' => {
  if (!tenantId) return 'user';
  if (roles.some(r => r.role === 'root_admin' && r.tenant_id === null)) return 'root_admin';
  if (roles.some(r => r.role === 'tenant_admin' && r.tenant_id === tenantId)) return 'tenant_admin';
  if (roles.some(r => r.role === 'tenant_supervisor' && r.tenant_id === tenantId)) return 'tenant_supervisor';
  if (roles.some(r => r.role === 'agent' && r.tenant_id === tenantId)) return 'agent';
  return 'user';
};

export function FinancialLayout({ children }: FinancialLayoutProps) {
  const { user, signOut, currentTenant, availableTenants, switchTenant, profile, refreshProfile, roles } = useAuth();
  useAssertNotSharedRoute('FinancialLayout');

  // FONTE ÚNICA DE VERDADE para flags de módulo no header (inclui Indicação).
  const { moduleSwitcherProps } = useHeaderModuleFlags();
  
  // Role do usuário no tenant atual
  const userRole = getUserRole(roles, currentTenant?.id);
  
  // Estado do modal de perfil
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  /**
   * Executa logout com redirecionamento
   */
  const handleLogout = () => {
    signOut(() => {
      window.location.href = '/auth';
    });
  };

  return (
    <PaymentGuard>
    <TooltipProvider>
      <SidebarProvider defaultOpen={true}>
        <div className="app-viewport-min flex w-full">
          <AppShellSidebar />
          {/* 
           * CONTENÇÃO FLEXBOX: min-w-0 é OBRIGATÓRIO aqui!
           */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <header className="h-12 sm:h-14 bg-card flex items-center gap-2 px-2 sm:px-4 mx-2 sm:mx-4 md:mx-6 mt-2 sm:mt-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink-0">
                <SidebarTrigger />
                <TenantSwitcher
                  availableTenants={availableTenants}
                  currentTenant={currentTenant}
                  onSwitch={switchTenant}
                />
              </div>

              {/* Bloco central: busca global + botão "+" */}
              <HeaderSearchSlot />

              {/* Espaçador no mobile */}
              <div className="flex-1 md:hidden" />

              {/* Ações do Header - itens secundários ocultos no mobile */}
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">

                {/* Menu overflow COMPLETO - apenas mobile (<768px) */}
                <div className="md:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="full" />
                </div>

                {/* Menu overflow AÇÕES - desktop compacto (768px–1280px) */}
                <div className="hidden md:block xl:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="actions-only" />
                </div>
                
                {/* Status do Agente - apenas xl+ */}
                
                
                {/* Toggle de Tema - apenas xl+ */}
                <div className="hidden xl:block">
                  <ThemeToggle />
                </div>
                
                {/* Notificações - sempre visível */}
                <AppLauncherButton />
                {/* No md+ o "+" já aparece ao lado da busca central */}
                <div className="md:hidden">
                  <QuickCreateButton />
                </div>

                <NotificationBell />
                
                {/* Menu de Usuário - sempre visível */}
                <UserMenu
                  user={user}
                  profile={profile}
                  userRole={userRole}
                  onOpenProfile={() => setProfileDialogOpen(true)}
                  onLogout={handleLogout}
                />
              </div>
            </header>
            
            {/* Main Content */}
            <main className="flex-1 min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-6 bg-muted/30">
              {children}
            </main>
          </div>
        </div>
        
        {/* Modal de Perfil */}
        <UserProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          userId={user?.id || ''}
          userEmail={user?.email || ''}
          currentName={profile?.full_name || null}
          currentAvatarPath={profile?.avatar_url || null}
          onSuccess={refreshProfile}
        />
        
      </SidebarProvider>
    </TooltipProvider>
    </PaymentGuard>
  );
}
